import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { DateInput } from "../components/ui/date-input";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { handleApiError } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import {
  Loader2, Banknote, TrendingDown, TrendingUp, Wallet, Layers,
  Calendar as CalendarIcon, ArrowLeftRight, ArrowDownCircle, ArrowUpCircle
} from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend
} from "recharts";
import { useTranslation } from "../contexts/I18nContext";

const fmtEUR = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);
const fmtEURFull = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);
const meseLabel = (yyyymm) => {
  if (!yyyymm) return "";
  const [y, m] = yyyymm.split('-');
  const nomi = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
  const idx = parseInt(m, 10) - 1;
  return idx >= 0 && idx < 12 ? `${nomi[idx]} ${y}` : yyyymm;
};
const today = () => new Date().toISOString().slice(0, 10);
const firstDayMonth = (offset = 0) => {
  const d = new Date(); d.setMonth(d.getMonth() + offset, 1);
  return d.toISOString().slice(0, 10);
};
const lastDayMonth = (offset = 0) => {
  const d = new Date(); d.setMonth(d.getMonth() + offset + 1, 0);
  return d.toISOString().slice(0, 10);
};
const firstDayYear = () => `${new Date().getFullYear()}-01-01`;

// ---------- Tooltip components ----------
const PieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-card border border-border rounded-lg shadow-lg p-3 text-sm">
      <div className="font-semibold" style={{ color: p.color }}>{p.name}</div>
      <div className="font-mono mt-1">{fmtEURFull(p.value)}</div>
    </div>
  );
};

const BarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const filtered = payload.filter(p => (p.value || 0) > 0).sort((a, b) => b.value - a.value);
  const totale = filtered.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div className="bg-card border border-border rounded-lg shadow-lg p-3 text-sm min-w-[260px]">
      <div className="font-semibold mb-2 border-b pb-1">{label}</div>
      {filtered.map((p, i) => (
        <div key={p.dataKey || p.name || i} className="flex justify-between gap-3 py-0.5">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: p.color }} />
            {p.name}
          </span>
          <span className="font-mono tabular-nums">{fmtEURFull(p.value)}</span>
        </div>
      ))}
      <div className="flex justify-between gap-3 pt-2 mt-1 border-t font-semibold">
        <span>Totale</span><span className="font-mono tabular-nums">{fmtEURFull(totale)}</span>
      </div>
    </div>
  );
};

// ---------- Generic flow section (Uscite o Entrate) ----------
function FlowSection({ data, accent }) {
  const { t } = useTranslation();
  const [selectedCat, setSelectedCat] = useState(null);
  // Helper: traduci label di categoria (fallback al label backend)
  const catLabel = (cat) => {
    const tx = t(`reports.categories.${cat.categoria}`);
    return tx && !tx.startsWith('reports.categories.') ? tx : cat.label;
  };
  const categorieAttive = data?.by_categoria || [];
  const pieData = useMemo(() => categorieAttive.map(c => ({ name: catLabel(c), value: c.importo, color: c.color, key: c.categoria })), [categorieAttive, t]);
  const stackedData = useMemo(() => (data?.by_mese || []).map(m => ({ mese: meseLabel(m.mese), ...m.per_categoria })), [data]);

  return (
    <div className="space-y-4">
      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className={`border-l-4 border-l-${accent}-500 bg-gradient-to-br from-${accent}-50/60 to-transparent dark:from-${accent}-950/20 hover:shadow-md transition-shadow`}>
          <CardContent className="pt-5 pb-4">
            <p className={`text-xs font-semibold text-${accent}-600 uppercase tracking-wider`}>{t('reports.cashFlows.kpiTotal')}</p>
            <p className={`text-3xl font-bold font-mono text-${accent}-600 mt-1`}>{fmtEUR(data?.totale || 0)}</p>
            <p className="text-xs text-muted-foreground mt-1">{data?.totale_movimenti || 0} {t('reports.cashFlows.movements')}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500 bg-gradient-to-br from-blue-50/60 to-transparent dark:from-blue-950/20 hover:shadow-md transition-shadow">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider">{t('reports.cashFlows.kpiTopCategory')}</p>
            <p className="text-lg font-bold mt-1 truncate" title={data?.categoria_top?.label}>{data?.categoria_top ? catLabel(data.categoria_top) : '—'}</p>
            <p className="text-xs text-muted-foreground mt-1 font-mono">{fmtEUR(data?.categoria_top?.importo || 0)} · {data?.categoria_top?.perc || 0}%</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500 bg-gradient-to-br from-emerald-50/60 to-transparent dark:from-emerald-950/20 hover:shadow-md transition-shadow">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">{t('reports.cashFlows.kpiDistinct')}</p>
            <p className="text-3xl font-bold mt-1">{categorieAttive.length}</p>
            <p className="text-xs text-muted-foreground mt-1">{(data?.by_conto || []).length} {t('reports.cashFlows.accounts')}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500 bg-gradient-to-br from-amber-50/60 to-transparent dark:from-amber-950/20 hover:shadow-md transition-shadow">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider">{t('reports.cashFlows.kpiUnallocated')}</p>
            <p className="text-2xl font-bold font-mono text-amber-600 mt-1">
              {fmtEUR(categorieAttive.find(c => c.categoria === 'non_allocato')?.importo || 0)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">{categorieAttive.find(c => c.categoria === 'non_allocato')?.perc || 0}{t('reports.cashFlows.percOfTotal')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Donut + Legend */}
      <div className="grid gap-4 lg:grid-cols-5">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Layers className="h-4 w-4 text-primary" />{t('reports.cashFlows.categoryDistribution')}</CardTitle>
            <CardDescription>{t('reports.cashFlows.categoryDistributionSubtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            {pieData.length === 0 ? (
              <div className="h-80 flex items-center justify-center text-muted-foreground">{t('reports.cashFlows.noData')}</div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={120} paddingAngle={2} dataKey="value" onClick={(d) => setSelectedCat(prev => prev === d.key ? null : d.key)}>
                    {pieData.map((entry, i) => (
                      <Cell key={entry.key || entry.name || i} fill={entry.color} className="cursor-pointer hover:opacity-80 transition-opacity" stroke={selectedCat === entry.key ? "#000" : "transparent"} strokeWidth={selectedCat === entry.key ? 2 : 0} />
                    ))}
                  </Pie>
                  <ReTooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-base">{t('reports.cashFlows.categoryDetail')}</CardTitle>
            <CardDescription>{t('reports.cashFlows.clickToFilter')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
              {categorieAttive.map((c) => (
                <button key={c.categoria} onClick={() => setSelectedCat(prev => prev === c.categoria ? null : c.categoria)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all hover:shadow-sm text-left ${selectedCat === c.categoria ? 'border-primary ring-2 ring-primary/20 bg-primary/5' : 'border-border bg-card'}`}>
                  <div className="w-3 h-10 rounded-sm flex-shrink-0" style={{ backgroundColor: c.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{catLabel(c)}</div>
                    <div className="text-xs text-muted-foreground">{c.perc}{t('reports.cashFlows.percOfTotal')}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-bold tabular-nums">{fmtEURFull(c.importo)}</div>
                    <div className="w-32 h-1.5 bg-muted rounded-full mt-1 overflow-hidden">
                      <div className="h-full transition-all" style={{ width: `${c.perc}%`, backgroundColor: c.color }} />
                    </div>
                  </div>
                </button>
              ))}
              {categorieAttive.length === 0 && <div className="text-center py-8 text-muted-foreground text-sm">Nessuna categoria nel periodo</div>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stacked Bar */}
      {stackedData.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><CalendarIcon className="h-4 w-4 text-primary" />{t('reports.cashFlows.monthlyTrend')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={360}>
              <BarChart data={stackedData} margin={{ top: 5, right: 20, left: 60, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="mese" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 12 }} />
                <ReTooltip content={<BarTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {categorieAttive.map((c) => (
                  <Bar key={c.categoria} dataKey={c.categoria} name={catLabel(c)} stackId="a" fill={c.color} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* By Conto contabile */}
      {data?.by_conto?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Wallet className="h-4 w-4 text-primary" />{t('reports.cashFlows.byAccountTitle')}</CardTitle>
            <CardDescription>{t('reports.cashFlows.byAccountSubtitle')} (top {Math.min(data.by_conto.length, 20)} di {data.by_conto.length})</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">{t('common.code')}</TableHead>
                    <TableHead>Nome Conto</TableHead>
                    <TableHead>{t('common.categories')}</TableHead>
                    <TableHead className="text-right w-[120px]">{t('common.amount')}</TableHead>
                    <TableHead className="text-right w-[80px]">%</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.by_conto.slice(0, 20).map((c, i) => (
                    <TableRow key={c.conto_id || `nn-${i}`}>
                      <TableCell className="font-mono text-xs">{c.conto_codice}</TableCell>
                      <TableCell className="text-sm">{c.conto_nome}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {c.categorie.slice(0, 3).map(cat => (
                            <Badge key={cat} variant="outline" className="text-xs">{cat.replace(/_/g, ' ')}</Badge>
                          ))}
                          {c.categorie.length > 3 && <Badge variant="outline" className="text-xs">+{c.categorie.length - 3}</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums font-semibold">{fmtEURFull(c.importo)}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline" className="font-mono">{c.perc}%</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* By Banca */}
      {data?.by_banca?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Banknote className="h-4 w-4 text-primary" />{t('reports.cashFlows.byBank')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t('dialogs.bank')}</TableHead>
                <TableHead className="text-right">N. movimenti</TableHead>
                <TableHead className="text-right">{t('common.total')}</TableHead>
                <TableHead className="text-right">%</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.by_banca.map((b) => {
                  const tot = data.by_banca.reduce((s, x) => s + x.importo, 0);
                  const perc = tot > 0 ? ((b.importo / tot) * 100).toFixed(1) : '0.0';
                  return (
                    <TableRow key={b.banca}>
                      <TableCell className="font-medium">{b.banca}</TableCell>
                      <TableCell className="text-right tabular-nums">{b.count}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums font-semibold">{fmtEURFull(b.importo)}</TableCell>
                      <TableCell className="text-right"><Badge variant="outline" className="font-mono">{perc}%</Badge></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------- Rendiconto ----------
function RendicontoSection({ rendiconto }) {
  const { t } = useTranslation();
  const catLabel = (cat) => {
    const tx = t(`reports.categories.${cat.categoria}`);
    return tx && !tx.startsWith('reports.categories.') ? tx : cat.label;
  };
  if (!rendiconto) return <div className="text-center py-8 text-muted-foreground">{t('common.loading')}</div>;
  const variazione = rendiconto.variazione_netta || 0;
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="border-l-4 border-l-slate-500 hover:shadow-md transition-shadow">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t('reports.cashFlows.kpiOpenBalance')}</p>
            <p className="text-2xl font-bold font-mono mt-1" data-testid="saldo-iniziale">{fmtEURFull(rendiconto.saldo_iniziale)}</p>
            <p className="text-xs text-muted-foreground mt-1">{t('reports.cashFlows.openBalanceAt')} {rendiconto.periodo.data_inizio}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500 bg-gradient-to-br from-emerald-50/60 to-transparent dark:from-emerald-950/20 hover:shadow-md transition-shadow">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">{t('reports.cashFlows.kpiInflowsBank')}</p>
            <p className="text-2xl font-bold font-mono text-emerald-600 mt-1">{fmtEURFull(rendiconto.entrate_periodo_banca)}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500 bg-gradient-to-br from-red-50/60 to-transparent dark:from-red-950/20 hover:shadow-md transition-shadow">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-semibold text-red-600 uppercase tracking-wider">{t('reports.cashFlows.kpiOutflowsBank')}</p>
            <p className="text-2xl font-bold font-mono text-red-600 mt-1">{fmtEURFull(rendiconto.uscite_periodo_banca)}</p>
          </CardContent>
        </Card>
        <Card className={`border-l-4 ${rendiconto.saldo_finale >= 0 ? 'border-l-emerald-600 bg-gradient-to-br from-emerald-100/60 to-transparent dark:from-emerald-950/30' : 'border-l-red-600 bg-gradient-to-br from-red-100/60 to-transparent dark:from-red-950/30'} hover:shadow-md transition-shadow`}>
          <CardContent className="pt-5 pb-4">
            <p className={`text-xs font-semibold uppercase tracking-wider ${rendiconto.saldo_finale >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{t('reports.cashFlows.kpiCloseBalance')}</p>
            <p className={`text-3xl font-bold font-mono mt-1 ${rendiconto.saldo_finale >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="saldo-finale">{fmtEURFull(rendiconto.saldo_finale)}</p>
            <p className="text-xs text-muted-foreground mt-1">{t('reports.cashFlows.variation')}: <span className={variazione >= 0 ? 'text-emerald-600' : 'text-red-600'}>{variazione >= 0 ? '+' : ''}{fmtEURFull(variazione)}</span></p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><ArrowLeftRight className="h-4 w-4 text-primary" />{t('reports.cashFlows.statementTitle')}</CardTitle>
          <CardDescription>{t('reports.cashFlows.statementSubtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-xl border overflow-hidden bg-card">
            <Table>
              <TableBody>
                <TableRow className="bg-slate-50 dark:bg-slate-900/40">
                  <TableCell colSpan={2} className="font-semibold">{t('reports.cashFlows.openBalanceAt')} {rendiconto.periodo.data_inizio}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums font-bold">{fmtEURFull(rendiconto.saldo_iniziale)}</TableCell>
                </TableRow>
                <TableRow><TableCell colSpan={3} className="py-1 text-xs text-muted-foreground italic pl-8">
                  {t('reports.cashFlows.opening')}: {fmtEURFull(rendiconto.saldo_banche_apertura)} {rendiconto.delta_pre_periodo !== 0 && <>+ {t('reports.cashFlows.prePeriodNet')}: {fmtEURFull(rendiconto.delta_pre_periodo)}</>}
                </TableCell></TableRow>

                {/* Entrate per categoria */}
                <TableRow className="bg-emerald-50/50 dark:bg-emerald-950/20">
                  <TableCell colSpan={2} className="font-semibold text-emerald-700"><ArrowDownCircle className="h-4 w-4 inline mr-1" />{t('reports.cashFlows.inflowsOfPeriod')}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums font-bold text-emerald-700">+ {fmtEURFull(rendiconto.entrate?.totale || 0)}</TableCell>
                </TableRow>
                {(rendiconto.entrate?.by_categoria || []).map(c => (
                  <TableRow key={`e-${c.categoria}`} className="hover:bg-muted/30">
                    <TableCell className="w-3 pl-8"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: c.color }} /></TableCell>
                    <TableCell className="text-sm">{catLabel(c)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-emerald-700">+ {fmtEURFull(c.importo)} <span className="text-xs text-muted-foreground ml-2">({c.perc}%)</span></TableCell>
                  </TableRow>
                ))}

                {/* Uscite per categoria */}
                <TableRow className="bg-red-50/50 dark:bg-red-950/20">
                  <TableCell colSpan={2} className="font-semibold text-red-700"><ArrowUpCircle className="h-4 w-4 inline mr-1" />{t('reports.cashFlows.outflowsOfPeriod')}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums font-bold text-red-700">− {fmtEURFull(rendiconto.uscite?.totale || 0)}</TableCell>
                </TableRow>
                {(rendiconto.uscite?.by_categoria || []).map(c => (
                  <TableRow key={`u-${c.categoria}`} className="hover:bg-muted/30">
                    <TableCell className="w-3 pl-8"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: c.color }} /></TableCell>
                    <TableCell className="text-sm">{catLabel(c)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-red-700">− {fmtEURFull(c.importo)} <span className="text-xs text-muted-foreground ml-2">({c.perc}%)</span></TableCell>
                  </TableRow>
                ))}

                {/* Saldo finale */}
                <TableRow className={`${rendiconto.saldo_finale >= 0 ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'} font-bold`}>
                  <TableCell colSpan={2} className="text-base">{t('reports.cashFlows.closeBalanceAt')} {rendiconto.periodo.data_fine}</TableCell>
                  <TableCell className={`text-right font-mono tabular-nums text-lg ${rendiconto.saldo_finale >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtEURFull(rendiconto.saldo_finale)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------- Main Page ----------
export default function UsciteCassaPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [uscite, setUscite] = useState(null);
  const [entrate, setEntrate] = useState(null);
  const [rendiconto, setRendiconto] = useState(null);
  const [dataInizio, setDataInizio] = useState(firstDayMonth(0));
  const [dataFine, setDataFine] = useState(today());
  const [includiGiroconti, setIncludiGiroconti] = useState(false);
  const [tab, setTab] = useState("rendiconto");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { data_inizio: dataInizio, data_fine: dataFine, includi_giroconti: includiGiroconti };
      const [u, e, r] = await Promise.all([
        api.get(`${getApiPrefix()}/reports/uscite-cassa`, { params }),
        api.get(`${getApiPrefix()}/reports/entrate-cassa`, { params }),
        api.get(`${getApiPrefix()}/reports/rendiconto-cassa`, { params: { data_inizio: dataInizio, data_fine: dataFine } }),
      ]);
      setUscite(u.data);
      setEntrate(e.data);
      setRendiconto(r.data);
    } catch (err) {
      handleApiError(err, "Errore caricamento report flussi cassa");
    } finally {
      setLoading(false);
    }
  }, [dataInizio, dataFine, includiGiroconti]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const applicaPreset = (preset) => {
    if (preset === 'mese_corr') { setDataInizio(firstDayMonth(0)); setDataFine(today()); }
    else if (preset === 'mese_prec') { setDataInizio(firstDayMonth(-1)); setDataFine(lastDayMonth(-1)); }
    else if (preset === 'trim') { setDataInizio(firstDayMonth(-2)); setDataFine(today()); }
    else if (preset === 'ytd') { setDataInizio(firstDayYear()); setDataFine(today()); }
    else if (preset === 'anno_prec') { const y = new Date().getFullYear() - 1; setDataInizio(`${y}-01-01`); setDataFine(`${y}-12-31`); }
  };

  if (loading && !uscite && !entrate) return <PageLoader />;

  return (
    <div className="space-y-6" data-testid="uscite-cassa-page">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-red-100 to-emerald-100 dark:from-red-900/30 dark:to-emerald-900/30 flex items-center justify-center">
          <ArrowLeftRight className="h-7 w-7 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('reports.cashFlows.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('reports.cashFlows.subtitle')}</p>
        </div>
      </div>

      {/* Filtri */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-end gap-4">
            <div><Label className="text-xs">{t('reports.cashFlows.filterFrom')}</Label><DateInput value={dataInizio} onChange={(e) => setDataInizio(e.target.value)} data-testid="filter-data-inizio" /></div>
            <div><Label className="text-xs">{t('reports.cashFlows.filterTo')}</Label><DateInput value={dataFine} onChange={(e) => setDataFine(e.target.value)} data-testid="filter-data-fine" /></div>
            <div className="flex flex-wrap gap-1">
              <Button variant="outline" size="sm" onClick={() => applicaPreset('mese_corr')}>{t('reports.cashFlows.presetCurrentMonth')}</Button>
              <Button variant="outline" size="sm" onClick={() => applicaPreset('mese_prec')}>{t('reports.cashFlows.presetPrevMonth')}</Button>
              <Button variant="outline" size="sm" onClick={() => applicaPreset('trim')}>{t('reports.cashFlows.presetLast3Months')}</Button>
              <Button variant="outline" size="sm" onClick={() => applicaPreset('ytd')} data-testid="preset-ytd">{t('reports.cashFlows.presetYTD')}</Button>
              <Button variant="outline" size="sm" onClick={() => applicaPreset('anno_prec')}>{t('reports.cashFlows.presetPrevYear')}</Button>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <Switch checked={includiGiroconti} onCheckedChange={setIncludiGiroconti} id="incl-girconti" />
              <Label htmlFor="incl-girconti" className="text-xs cursor-pointer">{t('reports.cashFlows.includeTransfers')}</Label>
            </div>
            <Button onClick={fetchAll} disabled={loading} data-testid="refresh-report">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}{t('reports.cashFlows.refresh')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="rendiconto" data-testid="tab-rendiconto"><ArrowLeftRight className="h-4 w-4 mr-1.5" />{t('reports.cashFlows.tabStatement')}</TabsTrigger>
          <TabsTrigger value="uscite" data-testid="tab-uscite"><TrendingDown className="h-4 w-4 mr-1.5" />{t('reports.cashFlows.tabOutflows')}</TabsTrigger>
          <TabsTrigger value="entrate" data-testid="tab-entrate"><TrendingUp className="h-4 w-4 mr-1.5" />{t('reports.cashFlows.tabInflows')}</TabsTrigger>
        </TabsList>
        <TabsContent value="rendiconto" className="space-y-4 mt-4">
          <RendicontoSection rendiconto={rendiconto} />
        </TabsContent>
        <TabsContent value="uscite" className="space-y-4 mt-4">
          <FlowSection data={uscite} accent="red" />
        </TabsContent>
        <TabsContent value="entrate" className="space-y-4 mt-4">
          <FlowSection data={entrate} accent="emerald" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
